import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  /** Names the part of the app in the console message. */
  label: string;
  /** A one-line notice, for a boundary around part of the page. */
  compact?: boolean;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches a render crash below it. Without one, React unmounts the whole app
 * and leaves a blank page with no hint of what went wrong. This shows the
 * error and a way to reload instead.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`${this.props.label} crashed:`, error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;

    if (error === null) {
      return this.props.children;
    }

    const reload = (
      <button
        type="button"
        className="button-generic text-sm"
        onClick={() => window.location.reload()}
      >
        Reload
      </button>
    );

    if (this.props.compact) {
      return (
        <div
          role="alert"
          className="flex-none flex items-center gap-3 px-3 sm:px-4 py-2 text-sm text-ink-2 bg-surface border-t border-rule"
        >
          <span>{this.props.label} stopped working.</span>
          {reload}
        </div>
      );
    }

    return (
      <div
        role="alert"
        className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center bg-bg text-ink"
      >
        <h1 className="text-lg font-semibold m-0">Something went wrong</h1>
        <p className="text-sm text-ink-2 m-0 max-w-md break-words">
          {error.message}
        </p>
        {reload}
      </div>
    );
  }
}
